<template>
  <div v-if="visible && current.popupImage" class="popup-ad__mask">
    <div class="popup-ad__wrap" :style="wrapStyle" @click.stop>
      <div v-if="showCountdownUi" class="popup-ad__countdown">{{ countdownLeft }}s</div>
      <img :src="current.popupImage" class="popup-ad__img" :style="imgStyle" @click="onClick" />
      <button type="button" class="popup-ad__close" aria-label="close" @click.stop="closePopup">×</button>
    </div>
  </div>
</template>

<script>
import { getActivePopups } from "@/api/upms/decoration";
import { navigateByLink } from "@/util/decorationNavigate";

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_WIDTH = 600;

export default {
  name: "PopupAd",
  data() {
    return {
      visible: false,
      current: {},
      queue: [],
      countdownLeft: 0,
      autoCloseTimer: null,
    };
  },
  computed: {
    showCountdownUi() {
      return this.countdownLeft > 0 && this.isShowCountdownEnabled(this.current);
    },
    wrapStyle() {
      const w = parseInt(this.current && this.current.popupWidth, 10);
      const h = parseInt(this.current && this.current.popupHeight, 10);
      const style = {};
      if (w > 0) style.width = w + "px";
      if (h > 0) style.height = h + "px";
      return style;
    },
    imgStyle() {
      const w = parseInt(this.current && this.current.popupWidth, 10);
      const h = parseInt(this.current && this.current.popupHeight, 10);
      const style = {};
      if (w > 0) style.maxWidth = w + "px";
      else style.maxWidth = DEFAULT_WIDTH + "px";
      if (h > 0) {
        style.height = h + "px";
        style.objectFit = "cover";
      } else {
        style.maxHeight = "80vh";
      }
      return style;
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.clearAutoClose();
  },
  methods: {
    isShowCountdownEnabled(popup) {
      if (!popup) return false;
      const v = popup.showCountdown;
      return v === true || v === 1 || v === "1" || v === "true";
    },
    effectiveAutoCloseSeconds(popup) {
      const sec = parseInt(popup && popup.autoCloseSeconds, 10);
      if (sec > 0) return sec;
      if (this.isShowCountdownEnabled(popup)) return 10;
      return 0;
    },
    load() {
      const userType = this.$store && this.$store.getters && this.$store.getters.userInfo
        ? (this.$store.getters.userInfo.userType || 0)
        : null;
      const params = userType !== null ? { targetUserType: userType } : {};
      getActivePopups(params)
        .then((r) => {
          const body = r && r.data;
          let list = [];
          if (body && Array.isArray(body.data)) list = body.data;
          else if (Array.isArray(body)) list = body;
          const valid = list.filter((p) => p && p.popupImage && String(p.popupImage).trim());
          const shown = valid.filter((p) => this.shouldShow(p));
          this.queue = shown;
          this.showNext();
        })
        .catch(() => {});
    },
    shouldShow(p) {
      if (!p || !p.id) return false;
      const key = this.frequencyKey(p);
      if (!key) return true;
      const last = localStorage.getItem(key);
      if (!last) return true;
      const lastTs = parseInt(last, 10);
      if (isNaN(lastTs)) return true;
      const days = this.frequencyDays(p);
      if (days <= 0) return true;
      return Date.now() - lastTs >= days * DAY_MS;
    },
    frequencyKey(p) {
      const type = p.displayFrequencyType;
      if (type === 1) return null;
      return "popup_shown_" + p.id;
    },
    frequencyDays(p) {
      const type = p.displayFrequencyType;
      if (type === 0) return 36500;
      if (type === 2) return Math.max(parseInt(p.displayFrequencyValue, 10) || 1, 1);
      return 0;
    },
    clearAutoClose() {
      if (this.autoCloseTimer) {
        clearInterval(this.autoCloseTimer);
        this.autoCloseTimer = null;
      }
      this.countdownLeft = 0;
    },
    startAutoClose() {
      this.clearAutoClose();
      const sec = this.effectiveAutoCloseSeconds(this.current);
      if (!sec || sec <= 0) return;
      this.countdownLeft = sec;
      this.autoCloseTimer = setInterval(() => {
        this.countdownLeft--;
        if (this.countdownLeft <= 0) {
          this.closePopup();
        }
      }, 1000);
    },
    showNext() {
      if (!this.queue.length) return;
      const next = this.queue.shift();
      if (!next || !next.popupImage) {
        this.showNext();
        return;
      }
      this.current = next;
      this.visible = true;
      this.$nextTick(() => this.startAutoClose());
      const key = this.frequencyKey(this.current);
      if (key) localStorage.setItem(key, String(Date.now()));
    },
    closePopup() {
      this.clearAutoClose();
      this.visible = false;
      this.current = {};
      this.$nextTick(() => this.showNext());
    },
    onClick() {
      if (this.current && this.current.linkUrl) {
        navigateByLink(this.$router, this.current.linkUrl);
      }
      this.closePopup();
    },
  },
};
</script>

<style scoped>
.popup-ad__mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
}
.popup-ad__wrap {
  position: relative;
  text-align: center;
  max-width: 90vw;
}
.popup-ad__countdown {
  position: absolute;
  top: 8px;
  left: 8px;
  min-width: 44px;
  padding: 0 10px;
  height: 28px;
  line-height: 28px;
  text-align: center;
  color: #fff;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 14px;
  z-index: 10;
  pointer-events: none;
}
.popup-ad__img {
  cursor: pointer;
  display: block;
  width: 100%;
  vertical-align: top;
  border: none;
  outline: none;
}
.popup-ad__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 11;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 24px;
  line-height: 32px;
  cursor: pointer;
}
.popup-ad__close:hover {
  background: rgba(0, 0, 0, 0.75);
}
</style>
